import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check if a JWT token is expired by decoding its payload
const isTokenExpired = (tkn) => {
  try {
    const payload = JSON.parse(atob(tkn.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    const stored = localStorage.getItem('token');
    if (stored && isTokenExpired(stored)) {
      localStorage.removeItem('token');
      return null;
    }
    return stored;
  });

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  // Axios interceptor: auto-logout on 401 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && token) {
          logout();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token, logout]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, phone, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, phone, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const addAddress = async (address) => {
    const response = await axios.post(`${API}/user/address`, address, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const updatedAddresses = [...(user.addresses || []), response.data.address];
    setUser({ ...user, addresses: updatedAddresses });
    return response.data;
  };

  const deleteAddress = async (addressId) => {
    await axios.delete(`${API}/user/address/${addressId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const updatedAddresses = user.addresses.filter(a => a.id !== addressId);
    setUser({ ...user, addresses: updatedAddresses });
  };

  const refreshUser = useCallback(async () => {
    if (token) {
      await fetchUser();
    }
  }, [token, fetchUser]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      updateUser,
      addAddress,
      deleteAddress,
      refreshUser,
      isAuthenticated: !!user,
      isAdmin: user?.is_admin || false,
      loyaltyPoints: user?.loyalty_points || 0,
      totalOrders: user?.total_orders || 0
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
